import { EditOutlined, MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Button,
  Checkbox,
  DatePicker,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  notification,
  Select,
  Space,
  Table,
  Tabs
} from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  competitionRegistrationsApi,
  competitionsApi,
  ranksApi,
  teamsApi,
  userClubsApi,
  usersApi,
} from "../../_helpers/apiService";
import { CURRENT_CLUB_ID } from "../../_helpers/constants";
import { ArcheryProp } from "../../_helpers/type";
import { PageTitle } from "../../components";
import FirestoreService from "../../_helpers/firestoreService";
import Archers from "./Archers";
import Board from "./Board";
import ByTeam from "./ByTeam";

const { Option } = Select;

interface DataType {
  key?: string;
  name?: string;
  date?: string;
}

const formItemLayout = {
  labelCol: { span: 6 },
  wrapperCol: { span: 14 },
};

function Competition() {
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [searchTeacher] = Form.useForm();
  const [data, setData] = useState<any>({});
  const [teams, setTeams] = useState<any[]>([]);
  const [searchVal, setSearchVal] = useState<string | null>(null);
  const [archersOptions, setArchersOptions] = useState<any[]>([]);
  const [competitors, setCompetitors] = useState<DataType[]>([]);
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(false);
  const [editVisible, setEditVisible] = useState(false);
  const [editConfirmLoading, setEditConfirmLoading] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const [isFinishedLoading, setIsFinishedLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [ranks, setRanks] = useState<any>([]);
  const [selectedArchers, setSelectedArchers] = useState<any[]>([]);
  const [referees, setReferees] = useState<any[]>([]);
  const userData = useSelector((state: any) => state.authentication.userData);
  let { competitionId } = useParams();

  // Fetch ranks and referees on mount
  useEffect(() => {
    const fetchRanks = async () => {
      try {
        const response = await ranksApi.getAll({
          club_id: CURRENT_CLUB_ID,
          limit: 1000,
          sort_by: "order",
          sort_order: "asc",
        });
        setRanks(response.data.items);
      } catch (error) {
        console.error("Error fetching ranks:", error);
        message.error("Цолын мэдээлэл татахад алдаа гарлаа");
      }
    };

    const fetchReferees = async () => {
      try {
        const response = await usersApi.getAll({
          role: "referee",
          limit: 1000,
        });
        setReferees(response.data.items);
      } catch (error) {
        console.error("Error fetching referees:", error);
        message.error("Шүүгчдийн мэдээлэл татахад алдаа гарлаа");
      }
    };

    fetchRanks();
    fetchReferees();
  }, []);

  // Fetch competition details and teams
  useEffect(() => {
    fetchCompetitionData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competitionId, ranks]);

  // Subscribe to Firestore competition metadata for real-time updates
  useEffect(() => {
    if (!competitionId) return;

    const unsubscribe = FirestoreService.subscribeToCompetitionMetadata(
      parseInt(competitionId),
      (metadata) => {
        if (metadata) {
          // Update local competition data with Firestore metadata
          setData((prevData: any) => ({
            ...prevData,
            ...(metadata.name ? { name: metadata.name } : {}),
            ...(metadata.location ? { location: metadata.location } : {}),
            ...(metadata.startDate ? { start_date: metadata.startDate, startDate: metadata.startDate } : {}),
            ...(metadata.endDate ? { end_date: metadata.endDate, endDate: metadata.endDate } : {}),
          }));
        }
      }
    );

    return () => {
      unsubscribe();
    };
  }, [competitionId]);

  const fetchCompetitionData = async () => {
    if (!competitionId) return;
    if (ranks.length === 0) return; // Wait for ranks to be loaded first

    setLoading(true);

    try {
      // Fetch competition details
      const competitionResponse = await competitionsApi.getById(
        parseInt(competitionId)
      );
      const competitionData = competitionResponse.data;

      // Check if referee has access to this competition
      if (userData.role === "referee") {
        const hasAccess =
          competitionData.referee_id === userData.id ||
          (competitionData.referees_ids && competitionData.referees_ids.includes(userData.id));

        if (!hasAccess) {
          message.error("Та энэ тэмцээнд хандах эрхгүй байна");
          window.location.href = "/competitions";
          return;
        }
      }

      setData(competitionData);
      setIsFinished(competitionData.status === "finished");

      // Fetch teams
      const teamsResponse = await teamsApi.getAll({
        club_id: CURRENT_CLUB_ID,
        limit: 1000,
      });
      setTeams(teamsResponse.data.items);

      // Fetch competition archers
      await fetchCompetitors();
    } catch (error) {
      console.error("Error fetching competition data:", error);
      message.error("Тэмцээний мэдээлэл татахад алдаа гарлаа");
    } finally {
      setLoading(false);
    }
  };

  const fetchCompetitors = async () => {
    if (!competitionId) return;

    try {
      // Use optimized endpoint with JOIN queries - fetches all data in a single request
      const response = await competitionsApi.getCompetitors(
        parseInt(competitionId!)
      );

      // The API might return either paginated response { items: [] } or array directly
      // Handle both cases to ensure backward compatibility
      let competitorsData: any[] = [];
      if (Array.isArray(response.data)) {
        competitorsData = response.data;
      } else if (response.data && typeof response.data === 'object' && 'items' in response.data) {
        competitorsData = (response.data as any).items || [];
      }

      // Transform API response to match component expectations
      const transformedCompetitors = competitorsData.map((competitor: any) => {
        // Get rank information from user_club data
        const currentRank = competitor.user_club?.rank || {
          id: competitor.user_club?.rank_id,
          name: "",
          order: 999
        };

        // Rate is stored in same rank field structure
        const currentRate = competitor.user_club?.rate || {
          id: competitor.user_club?.rate_id,
          name: "",
          order: 999
        };

        // Get team information from user_club data
        const teamInfo = competitor.user_club?.team || {
          id: competitor.user_club?.team_id,
          name: ""
        };

        return {
          key: competitor.id.toString(),
          id: competitor.id,
          registrationId: competitor.registration_id,
          firstName: competitor.first_name || "",
          lastName: competitor.last_name || "",
          birthDate: competitor.birth_date,
          rankId: competitor.user_club?.rank_id,
          teamId: competitor.user_club?.team_id,
          rank: currentRank,
          rankDate: competitor.user_club?.rank_date,
          rank2: currentRate,
          team: teamInfo,
          gender: competitor.gender || "male",
          ssn: competitor.ssn || "",
          rateDate: competitor.user_club?.rank_date || null,
          rateDate2: competitor.user_club?.rate_date || null,
          hanaPoints: [], // Initialize empty for now, will be filled from zurkhai
          hasPoints: [], // Initialize empty for now, will be filled from zurkhai
        };
      });

      setCompetitors(transformedCompetitors);
    } catch (error) {
      console.error("Error fetching competitors:", error);
      message.error("Харваачдын мэдээлэл татахад алдаа гарлаа");
    }
  };

  const fetchAllArchers = async () => {
    try {
      // Fetch ALL archers from the club
      const response = await userClubsApi.getWithDetails(CURRENT_CLUB_ID, {
        role: "archer",
        limit: 1000,
      });

      const archersArr = response.data.items.map((membership: any) => {
        const currentRank = ranks.find(
          (item: any) => item.id === membership.rank_id
        );
        const currentRate = ranks.find(
          (item: any) => item.id === membership.rate_id
        );

        // Find the team name
        const archerTeam = teams.find((team: any) => team.id === membership.team_id);

        return {
          key: membership.user_id.toString(),
          ssn: membership.ssn || "",
          email: membership.email || "",
          firstName: membership.first_name || "",
          lastName: membership.last_name || "",
          gender: membership.gender || "male",
          phoneNumber: membership.phone_number || "",
          teamId: membership.team_id,
          teamName: archerTeam?.name || "",
          rankId: membership.rank_id,
          rateId: membership.rate_id,
          rankName: currentRank?.name || "",
          rateName: currentRate?.name || "",
          isTeamLeader: membership.is_team_leader || false,
        };
      });

      setArchersOptions(archersArr);
    } catch (error) {
      console.error("Error fetching archers:", error);
      message.error("Харваачдын мэдээлэл татахад алдаа гарлаа");
    }
  };

  const handleCancel = () => {
    setVisible(false);
    setSelectedArchers([]);
    setArchersOptions([]);
    form.resetFields();
  };

  const handleOk = async () => {
    if (selectedArchers.length === 0) {
      notification.error({
        message: "Алдаа",
        description: "Харваачид сонгоно уу!",
      });
      return;
    }

    setConfirmLoading(true);
    try {
      await form.validateFields();

      if (!competitionId) {
        throw new Error("Competition ID is missing");
      }

      // Create competition registrations for each selected archer
      const registrationPromises = selectedArchers.map(
        async (archerKey: any) => {
          // Find archer details from archersOptions
          const archer = archersOptions.find((a) => a.key === archerKey);
          if (!archer) {
            throw new Error(`Archer with key ${archerKey} not found`);
          }

          return competitionRegistrationsApi.create({
            competition_id: parseInt(competitionId!),
            user_id: parseInt(archerKey),
            user_name: `${archer.lastName} ${archer.firstName}`,
          });
        }
      );

      await Promise.all(registrationPromises);

      notification.success({
        message: "Амжилттай",
        description: `${selectedArchers.length} харваач нэмэгдлээ.`,
      });

      setVisible(false);
      setSelectedArchers([]);
      setArchersOptions([]);
      form.resetFields();

      // Refresh competitors list
      fetchCompetitors();
    } catch (error: any) {
      console.error("Error adding archers:", error);
      const errorMsg =
        error.response?.data?.detail || "Харваач нэмэхэд алдаа гарлаа";
      message.error(errorMsg);
    } finally {
      setConfirmLoading(false);
    }
  };

  const onTeamSelect = async (teamId: string) => {
    console.log(`Team select ${teamId}`);
    setSelectedArchers([]); // Clear previous selections when team changes

    try {
      // Fetch ALL archers from the club
      const response = await userClubsApi.getWithDetails(CURRENT_CLUB_ID, {
        role: "archer",
        limit: 1000,
      });

      let archersArr = response.data.items.map((membership: any) => {
        const currentRank = ranks.find(
          (item: any) => item.id === membership.rank_id
        );
        const currentRate = ranks.find(
          (item: any) => item.id === membership.rate_id
        );

        // Find the team name
        const archerTeam = teams.find((team: any) => team.id === membership.team_id);

        return {
          key: membership.user_id.toString(),
          ssn: membership.ssn || "",
          email: membership.email || "",
          firstName: membership.first_name || "",
          lastName: membership.last_name || "",
          gender: membership.gender || "male",
          phoneNumber: membership.phone_number || "",
          teamId: membership.team_id,
          teamName: archerTeam?.name || "",
          rankId: membership.rank_id,
          rateId: membership.rate_id,
          rankName: currentRank?.name || "",
          rateName: currentRate?.name || "",
          isTeamLeader: membership.is_team_leader || false,
        };
      });

      // Filter by team if a specific team is selected (not "all")
      if (teamId && teamId !== "all") {
        archersArr = archersArr.filter((archer: any) => archer.teamId === parseInt(teamId));
      }

      setArchersOptions(archersArr);
    } catch (error) {
      console.error("Error fetching archers:", error);
      message.error("Харваачдын мэдээлэл татахад алдаа гарлаа");
    }
  };

  const removeArcher = async (archer: ArcheryProp) => {
    if (!archer.registrationId) {
      message.error("Бүртгэлийн дугаар олдсонгүй");
      return;
    }

    try {
      await competitionRegistrationsApi.delete(archer.registrationId);
      message.success(`${archer.lastName} ${archer.firstName} харваач амжилттай хасагдлаа`);

      // Refresh competitors list
      fetchCompetitors();
    } catch (error: any) {
      console.error("Error removing archer:", error);
      const errorMsg = error.response?.data?.detail || "Харваач хасахад алдаа гарлаа";
      message.error(errorMsg);
    }
  };

  // Validation function to check for duplicate referees in zurkhai assignments
  function hasDuplicates(data: any) {
    const refereeIds = new Set();

    for (let i = 0; i < data.length; i++) {
      const item = data[i];

      // Check if the current item has a refereeId property
      if (item?.refereeId) {
        const refereeId = item.refereeId;

        // If the refereeId is already in the Set, it's a duplicate
        if (refereeIds.has(refereeId)) {
          return true;
        }

        // Add the refereeId to the Set
        refereeIds.add(refereeId);
      }
    }

    // No duplicates found
    return false;
  }

  const checkRefs = (_: any, value: any) => {
    if (!hasDuplicates(value)) {
      return Promise.resolve();
    }
    return Promise.reject(
      new Error("Нэг шүүгчийг олон зурхай дээр сонгох боломжгүй!")
    );
  };

  const onCompFinish = async () => {
    if (!competitionId) return;

    Modal.confirm({
      title: "Тэмцээн дуусгах",
      content: "Та тэмцээнийг дуусгахдаа итгэлтэй байна уу? Дууссаны дараа оноо засах боломжгүй болно.",
      okText: "Тийм, дуусгах",
      cancelText: "Үгүй",
      okButtonProps: { danger: true },
      onOk: async () => {
        setIsFinishedLoading(true);
        try {
          await competitionsApi.update(parseInt(competitionId), {
            status: "finished",
          });

          setIsFinished(true);
          notification.success({
            message: "Амжилттай",
            description: `Тэмцээн амжилттай дууслаа.`,
          });
        } catch (error) {
          console.error("Error finishing competition:", error);
          notification.error({
            message: "Амжилтгүй",
            description: `Алдаа гарлаа.`,
          });
        } finally {
          setIsFinishedLoading(false);
        }
      },
    });
  };

  const handleEditCancel = () => {
    setEditVisible(false);
    editForm.resetFields();
  };

  const handleEditOk = async () => {
    if (!competitionId) return;

    setEditConfirmLoading(true);
    try {
      await editForm.validateFields();
      const values = editForm.getFieldsValue();

      // Convert dates to ISO strings
      const startDate = values.startDate
        ? dayjs(values.startDate).toISOString()
        : undefined;
      const endDate = values.endDate ? dayjs(values.endDate).toISOString() : undefined;
      const endRegisterDate = values.endRegisterDate
        ? dayjs(values.endRegisterDate).toISOString()
        : undefined;

      // Extract referee IDs from the referees array
      const refereesIds = values.referees?.map((referee: any) => {
        return parseInt(referee.refereeId);
      }) || [];

      await competitionsApi.update(parseInt(competitionId), {
        name: values.name,
        location: values.location,
        start_date: startDate,
        end_date: endDate,
        end_register_date: endRegisterDate,
        zurhai: values.zurhai,
        onog_ue: parseInt(values.onogUe),
        fee: parseInt(values.fee),
        detail_link: values.detailLink || undefined,
        guidance: values.guidance || undefined,
        safety: values.safety || undefined,
        is_rank: values.isRank || false,
        referee_id: values.referee ? parseInt(values.referee) : undefined,
        referees_ids: refereesIds,
      });

      // Also update Firestore metadata for real-time sync
      try {
        await FirestoreService.updateCompetitionMetadata(parseInt(competitionId), {
          name: values.name,
          location: values.location,
          startDate: startDate,
          endDate: endDate,
          zurkhaiCount: values.zurhai,
          onogUe: parseInt(values.onogUe),
        });
      } catch (firestoreError) {
        console.error("Error updating Firestore metadata:", firestoreError);
        // Don't fail the entire update if Firestore update fails
        // The real-time listener will still work if Firestore was already initialized
      }

      notification.success({
        message: "Амжилттай",
        description: "Тэмцээний мэдээлэл амжилттай шинэчлэгдлээ.",
      });

      setEditVisible(false);
      editForm.resetFields();

      // Refresh competition data
      fetchCompetitionData();
    } catch (error: any) {
      console.error("Error updating competition:", error);
      const errorMsg =
        error.response?.data?.detail || "Тэмцээн шинэчлэхэд алдаа гарлаа";
      message.error(errorMsg);
    } finally {
      setEditConfirmLoading(false);
    }
  };

  return (
    <>
      {data ? (
        <PageTitle
          title={data?.name}
          description={dayjs(data?.start_date).format("YYYY-MM-DD")}
          loading={loading}
          right={
            <Space wrap size="middle">
              {(userData.role === "admin" || userData.role === "superadmin") && !isFinished && (
                <Button
                  shape="round"
                  onClick={() => {
                    setEditVisible(true);
                    // Convert referees_ids array to form structure
                    const refereesFormData = (data.referees_ids || []).map((refId: number) => ({
                      refereeId: refId.toString(),
                    }));

                    editForm.setFieldsValue({
                      name: data.name,
                      location: data.location,
                      startDate: data.start_date ? dayjs(data.start_date) : null,
                      endDate: data.end_date ? dayjs(data.end_date) : null,
                      endRegisterDate: data.end_register_date
                        ? dayjs(data.end_register_date)
                        : null,
                      zurhai: data.zurhai,
                      onogUe: data.onog_ue,
                      fee: data.fee,
                      detailLink: data.detail_link,
                      guidance: data.guidance,
                      safety: data.safety,
                      isRank: data.is_rank,
                      referee: data.referee_id?.toString(),
                      referees: refereesFormData,
                    });
                  }}
                  icon={<EditOutlined />}
                  size="large"
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  Засах
                </Button>
              )}
              {userData.role !== "referee" && !isFinished && (
                <Button
                  shape="round"
                  onClick={() => {
                    setVisible(true);
                    fetchAllArchers();
                  }}
                  icon={<PlusOutlined />}
                  size="large"
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  Харваач нэмэх
                </Button>
              )}
              {(userData.role === "admin" || userData.role === "superadmin") && !isFinished && (
                <Button
                  shape="round"
                  onClick={() => onCompFinish()}
                  size="large"
                  loading={isFinishedLoading}
                >
                  Тэмцээн дуусгах
                </Button>
              )}
            </Space>
          }
        />
      ) : (
        ""
      )}
      <div className="page-content-wrapper">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="card">
                <div className="card-body">
                  {userData.role !== "referee" && (
                    <>
                      <p>Нийт харваачдын тоо: {competitors.length}</p>
                      <p>Зурхайн нийт тоо: {data?.zurhai || 0}</p>
                      <p>
                        Улсын чанартай тэмцээн:{" "}
                        {data.is_rank === true ? "Тийм" : "Үгүй"}
                      </p>
                    </>
                  )}
                  <Tabs
                    defaultActiveKey="3"
                    items={[
                      ...(userData.role !== "referee"
                        ? [
                            {
                              key: "1",
                              label: "Харваачид",
                              children: (
                                <Archers
                                  competitors={competitors}
                                  loading={loading}
                                  ranks={ranks}
                                  teams={teams}
                                  removeArcher={removeArcher}
                                  competitionId={competitionId}
                                />
                              ),
                            },
                          ]
                        : []),
                      ...((userData.role === "admin" || userData.role === "superadmin")
                        ? [
                            {
                              key: "2",
                              label: "Багаар",
                              children: (
                                <ByTeam
                                  competitors={competitors}
                                  ranks={ranks}
                                  teams={teams}
                                  loading={loading}
                                  zurhaiTotal={data?.zurhai || 0}
                                />
                              ),
                            },
                          ]
                        : []),
                      ...(userData.role !== "leader"
                        ? [
                            {
                              key: "3",
                              label: "Самбар",
                              children: (
                                <Board
                                  competition={data}
                                  competitors={competitors}
                                  ranks={ranks}
                                  teams={teams}
                                  referees={data?.referees_ids || []}
                                  loading={loading}
                                  zurhaiTotal={data?.zurhai || 0}
                                  currentUserId={userData.id}
                                  userRole={userData.role}
                                  isFinished={isFinished}
                                />
                              ),
                            },
                          ]
                        : []),
                    ]}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        title="Харваач нэмэх"
        open={visible}
        onOk={handleOk}
        onCancel={handleCancel}
        okText="Нэмэх"
        cancelText="Болих"
        width={900}
        confirmLoading={confirmLoading}
      >
        <Form {...formItemLayout} form={form}>
          <Form.Item
            label="Баг"
            name="team"
          >
            <Select placeholder="Баг" allowClear onChange={onTeamSelect} defaultValue="all">
              <Option key="all" value="all">
                Бүгд
              </Option>
              {teams
                .sort((a, b) => (a.name > b.name ? 1 : -1))
                .map((team) => {
                  return (
                    <Option key={team.id} value={team.id.toString()}>
                      {team.name}
                    </Option>
                  );
                })}
            </Select>
          </Form.Item>
        </Form>

        <div style={{ marginTop: 16 }}>
          <h4>Харваачид</h4>
          <Table
            dataSource={archersOptions}
            columns={[
              {
                title: "Нэр",
                dataIndex: "firstName",
                key: "firstName",
                width: 150,
                render: (_, record) => `${record.lastName} ${record.firstName}`,
              },
              {
                title: "Баг",
                dataIndex: "teamName",
                key: "teamName",
                width: 150,
              },
              {
                title: "Цол",
                dataIndex: "rankName",
                key: "rankName",
                width: 150,
              },
              {
                title: "Утас",
                dataIndex: "phoneNumber",
                key: "phoneNumber",
                width: 130,
                render: (phoneNumber) => {
                  // Remove +976 prefix if it exists
                  if (phoneNumber && phoneNumber.startsWith("+976")) {
                    return phoneNumber.substring(4);
                  }
                  return phoneNumber;
                },
              },
            ]}
            rowSelection={{
              selectedRowKeys: selectedArchers,
              onChange: (selectedRowKeys) => {
                setSelectedArchers(selectedRowKeys);
              },
              getCheckboxProps: (record) => {
                // Disable checkbox if archer is already registered in the competition
                const isRegistered = competitors.some((comp: any) => comp.id.toString() === record.key);
                return {
                  disabled: isRegistered,
                };
              },
            }}
            pagination={{
              pageSize: 50,
              showSizeChanger: false,
              showTotal: (total, range) => `${range[0]}-${range[1]} / ${total}`,
            }}
            scroll={{ x: "max-content", y: 300 }}
            size="small"
          />
        </div>
      </Modal>

      {/* Edit Competition Modal */}
      <Modal
        title="Тэмцээн засах"
        open={editVisible}
        onOk={handleEditOk}
        onCancel={handleEditCancel}
        okText="Хадгалах"
        cancelText="Болих"
        width={720}
        confirmLoading={editConfirmLoading}
      >
        <Form {...formItemLayout} form={editForm}>
          <Form.Item
            label="Улсын чанартай эсэх:"
            name="isRank"
            valuePropName="checked"
          >
            <Checkbox />
          </Form.Item>
          <Form.Item
            label="Нэр"
            name="name"
            rules={[{ required: true, message: "Нэр оруулна уу!" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Байршил"
            name="location"
            rules={[{ required: true, message: "Байршил оруулна уу!" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Эхлэх огноо"
            name="startDate"
            rules={[{ required: true, message: "Эхлэх огноо сонгоно уу!" }]}
          >
            <DatePicker />
          </Form.Item>
          <Form.Item
            label="Дуусах огноо"
            name="endDate"
            rules={[{ required: true, message: "Дуусах огноо сонгоно уу!" }]}
          >
            <DatePicker />
          </Form.Item>
          <Form.Item
            label="Бүртгэл дуусах огноо"
            name="endRegisterDate"
            rules={[
              { required: true, message: "Бүртгэл дуусах огноо сонгоно уу!" },
            ]}
          >
            <DatePicker />
          </Form.Item>
          <Form.Item
            label="Зурхайн тоо"
            name="zurhai"
            rules={[{ required: true, message: "Зурхайн тоо оруулна уу!" }]}
          >
            <InputNumber min={0} />
          </Form.Item>
          <Form.Item
            label="Оног үе"
            name="onogUe"
            rules={[{ required: true, message: "Оног үе оруулна уу!" }]}
          >
            <InputNumber min={1} />
          </Form.Item>
          <Form.List
            name="referees"
            rules={[
              {
                validator: async (_, refers) => checkRefs(_, refers)
              }
            ]}
          >
            {(fields, { add, remove }, { errors }) => (
              <>
                {fields.map((field, index) => (
                  <Form.Item
                    noStyle
                    key={field.key}
                    shouldUpdate={(prevValues, curValues) =>
                      prevValues.zurhai !== curValues.zurhai
                    }
                  >
                    {() => (
                      <Space.Compact block>
                        <Form.Item
                          style={{ width: "100%" }}
                          label={`#${index + 1} зурхайн шүүгч:`}
                          name={[field.name, `refereeId`]}
                          rules={[
                            {
                              required: true,
                              message: "Зурхайн шүүгч сонгоно уу!"
                            }
                          ]}
                        >
                          <Select placeholder="Зурхайн шүүгч сонгох" allowClear>
                            {referees.map((referee) => (
                              <Option key={referee.id} value={referee.id.toString()}>
                                {referee.last_name} {referee.first_name}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                        <Space align="center" style={{ height: 30 }}>
                          <MinusCircleOutlined
                            onClick={() => remove(field.name)}
                          />
                        </Space>
                      </Space.Compact>
                    )}
                  </Form.Item>
                ))}

                <Form.Item
                  wrapperCol={{
                    span: 14,
                    offset: 6
                  }}
                  style={{ width: "100%" }}
                >
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    block
                    icon={<PlusOutlined />}
                  >
                    Зурхай нэмэх
                  </Button>
                  <Form.ErrorList errors={errors} />
                </Form.Item>
              </>
            )}
          </Form.List>
          <Form.Item
            label="Ерөнхий шүүгч"
            name="referee"
            rules={[{ required: true, message: "Ерөнхий шүүгч оруулна уу!" }]}
          >
            <Select placeholder="Ерөнхий шүүгч сонгох" allowClear>
              {referees.map((referee) => (
                <Option key={referee.id} value={referee.id.toString()}>
                  {referee.last_name} {referee.first_name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            label="Төлбөр"
            name="fee"
            rules={[{ required: true, message: "Төлбөр оруулна уу!" }]}
          >
            <InputNumber />
          </Form.Item>
          <Form.Item label="Линк" name="detailLink">
            <Input />
          </Form.Item>
          <Form.Item label="Удирдамж" name="guidance">
            <Input />
          </Form.Item>
          <Form.Item label="Аюулгүй ажиллагаа" name="safety">
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
export default Competition;
